import {Component, OnDestroy, OnInit, Input} from '@angular/core';
import {Plot, MonitorShortLabels} from "../../model/plot";
import {PlotsService} from "../../services/plots.service";
import {Observable, Subscription, firstValueFrom} from "rxjs";
import {LabeledFieldComponent} from "../labeled-field/labeled-field.component";
import {SortMonitorsPipe} from "../../pipes/sort-monitors.pipe";
import {FeatherModule} from "angular-feather";
import {CommonModule} from "@angular/common";
import {selectedPlotsChange, stateSet} from '../../events';
import {StatesService} from '../../services/states.service';
import {metadataLabels} from "../../constants";
import {Metadata, MetadataLabel, MetadataValue} from "../../model";
import {BooleanPipe} from "../../pipes/boolean.pipe";

@Component({
    selector: 'app-plot-details',
    templateUrl: './plot-details.component.html',
    imports: [
        BooleanPipe,
        CommonModule,
        LabeledFieldComponent,
        SortMonitorsPipe,
        FeatherModule
    ],
    styleUrls: ['./plot-details.component.scss']
})
export class PlotDetailsComponent implements OnInit, OnDestroy {
  @Input() userIsCustomer: boolean = false;

  plot: Plot | null = null;
  metadata$: Observable<Metadata | null> | undefined;
  stateMetadataLabels: MetadataLabel[] = [];
  assessmentTags: any[] = [];

  subtables: any = {};

  plotChangedSubscription: Subscription | undefined;
  stateChangedSubscription: Subscription | undefined;

  math = Math;

  finalResultsExpanded = true;
  resultsExpanded = true;

  expandedFinalResults: {monitor: string; updatedAt: number}[] = [];
  expandedResults: {monitor: string; updatedAt: number}[] = [];


  constructor(private plotsService: PlotsService,
    private statesService: StatesService
  ) {
  }

  async ngOnInit() {
    if (!this.userIsCustomer) {
      this.assessmentTags = await firstValueFrom(this.plotsService.getAssessmentTags());
    }
    const activeState = this.statesService.getActiveState();
    this.stateMetadataLabels = metadataLabels.find(state => state.id === activeState?.id)?.labels!;

    this.stateChangedSubscription = stateSet.subscribe(newState => {
      this.stateMetadataLabels = metadataLabels.find(state => state.id === newState?.id)?.labels!;
      this.plot = null;
    });

    this.plotChangedSubscription = selectedPlotsChange
      .subscribe(async data => {
        if (data.plotIds.length >= 1) {
          await this.getData(data.plotIds[0]);
        } else {
          this.plot = null;
        }
      });
  }

  private async getData(plotId: number) {
    try {
      this.expandedFinalResults = [];
      this.expandedResults = [];
      this.plot = await firstValueFrom(this.plotsService.getPlotDetails(plotId, this.userIsCustomer));

      if (this.userIsCustomer) {
        this.plot?.finalResults.forEach(finalResult => {
          if (finalResult && finalResult.updatedAt) {
            this.toggleExpandFinalResult(finalResult.monitor, new Date(finalResult.updatedAt))
          }
        });
      }
      
      if (this.stateMetadataLabels.length !== 0) {
        this.metadata$ = this.plotsService.getPlotMetadata(plotId, this.userIsCustomer, this.stateMetadataLabels);
      }
    } catch (e) {
      console.error(e);
      // showNotification.next({ title: 'Error', message: `1 No plot details for ID ${plotId}.` + e });
    }
    
  }

  metadataRows(metadata: {[key: string]: MetadataValue;}): number[] {
    const rows = [];
    for (const key of Object.keys(metadata)) {
        rows.push(metadata[key].row);
    }
    return rows.filter((value, index, self) => self.indexOf(value) === index).sort();
  }

  ngOnDestroy() {
    this.plotChangedSubscription?.unsubscribe();
    this.stateChangedSubscription?.unsubscribe();
  }

  hasChildren(obj: any): boolean {
    return obj !== null && typeof obj === 'object' && Object.keys(obj).length > 0;
  }

  isArray(obj: any): boolean {
    return obj.hasOwnProperty('length');
  }

  getKeys(obj: any) {
    return Object.keys(obj);
  }

  expand(key: string) {
    this.subtables[key] = !this.subtables[key];
  }

  isExpanded(key: string): boolean {
    return !!this.subtables[key];
  }

  isFinalResultExpanded(monitor: string | undefined, updatedAt: Date | undefined) {
    const index = this.expandedFinalResults.findIndex(result => result.monitor === monitor && result.updatedAt === updatedAt?.valueOf());
    return updatedAt && index !== -1;
  }

  toggleExpandFinalResults() {
    this.finalResultsExpanded = !this.finalResultsExpanded;
  }

  toggleExpandResults() {
    this.resultsExpanded = !this.resultsExpanded;
  }

  toggleExpandFinalResult(monitor: string | undefined, updatedAt: Date | undefined) {
    if (updatedAt) {
      const index = this.expandedFinalResults.findIndex(result => result.monitor === monitor && result.updatedAt === updatedAt.valueOf());
      if (index !== -1) {
        this.expandedFinalResults.splice(index, 1);
      } else {
        this.expandedFinalResults.push({monitor: monitor || '', updatedAt: updatedAt.valueOf()});
      }
    }
  }

  isResultExpanded(monitor: string | undefined, updatedAt: Date | undefined) {
    const index = this.expandedResults.findIndex(result => result.monitor === monitor && result.updatedAt === updatedAt?.valueOf());
    return updatedAt && index !== -1;
  }

  toggleExpandResult(monitor: string | undefined, updatedAt: Date | undefined) {
    if (updatedAt) {
      const index = this.expandedResults.findIndex(result => result.monitor === monitor && result.updatedAt === updatedAt.valueOf());
      if (index !== -1) {
        this.expandedResults.splice(index, 1);
      } else {
        this.expandedResults.push({monitor: monitor || '', updatedAt: updatedAt.valueOf()});
      }
    }
  }

  getMonitorShortLabel(field: string | undefined) {
    if (field && ((MonitorShortLabels as any)[field])) {
      return (MonitorShortLabels as any)[field];
    } else {
      return field;
    }
  }

  getCodeColor(code: number | undefined, monitor?: string | undefined) {
    if (code === undefined) {
      return 'has-background-white';
    }

    const codeCategory = code ? parseInt(code.toString().slice(-2), 10) : 0;
    if (codeCategory < 10 || (monitor && monitor === 'KENN' && codeCategory >= 74 && codeCategory <= 78)) {
      return 'has-background-success';
    } else if (codeCategory >= 10 && codeCategory < 90) {
      return 'has-background-warning';
    } else {
      return 'has-background-danger';
    }
  }

  translateCodeColor(color: string | undefined) {
    if (color === undefined) {
      return 'has-background-white';
    }

    if (color === 'green') {
      return 'has-background-success';
    } else if (color === 'yellow' || color === 'orange') {
      return 'has-background-warning';
    } else {
      return 'has-background-danger';
    }
  }
  
  protected readonly Math = Math;
}
